import { useEffect, useRef } from 'react';
import { createConsumer, type Consumer } from '@rails/actioncable';
import { getActionCableUrl } from '@/utils/cableUrl';
import { queryClient } from '@/providers/AllProviders';
import { useAuth } from '@/providers/context/AuthProvider';
import { agencyNotificationQueryKeys } from '@/apis/agency/notifications/hook';
import { businessNotificationQueryKeys } from '@/apis/business/notifications/hook';

type CablePayload = {
  event?: string;
  notification?: Record<string, unknown>;
};

function invalidateNotificationQueries() {
  void queryClient.invalidateQueries({ queryKey: agencyNotificationQueryKeys.list() });
  void queryClient.invalidateQueries({ queryKey: agencyNotificationQueryKeys.unread() });

  const businessListRoot = businessNotificationQueryKeys.list(null)[0];
  const businessUnreadRoot = businessNotificationQueryKeys.unread(null)[0];
  void queryClient.invalidateQueries({ queryKey: [businessListRoot] });
  void queryClient.invalidateQueries({ queryKey: [businessUnreadRoot] });
}

/**
 * Subscribes to `NotificationsChannel` when the user is authenticated.
 * Relies on the HttpOnly session cookie being sent to the API host on the WebSocket handshake.
 */
export function useNotificationsRealtime() {
  const { isAuthenticated } = useAuth();
  const consumerRef = useRef<Consumer | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      consumerRef.current?.disconnect();
      consumerRef.current = null;
      return;
    }

    const cableUrl = getActionCableUrl();
    if (!cableUrl) {
      console.warn('[notifications cable] BACKEND_URL missing; skipping WebSocket');
      return;
    }

    let consumer: Consumer;
    try {
      consumer = createConsumer(cableUrl);
    } catch (e) {
      console.warn('[notifications cable] connect error', e);
      return;
    }
    consumerRef.current = consumer;

    const subscription = consumer.subscriptions.create(
      { channel: 'NotificationsChannel' },
      {
        received(data: unknown) {
          const payload = data as CablePayload;
          if (payload?.event !== 'notification.created') return;
          invalidateNotificationQueries();
        },
        disconnected() {
          // optional: could schedule reconnect; ActionCable client reconnects by default in some setups
        },
        rejected() {
          console.warn('[notifications cable] subscription rejected (check auth / tenant host)');
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      consumer.disconnect();
      if (consumerRef.current === consumer) {
        consumerRef.current = null;
      }
    };
  }, [isAuthenticated]);
}
